/**
 * Timetable data service.
 * Centralizes fetching, parsing and caching of the Google-Sheets-backed
 * timetable payloads used by App, Timetable and Classrooms.
 */

export const SHEET_CONFIG_URL = 'https://sahillatif0.github.io/mnoprs/abc.json';

export const DEFAULT_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1A1p89c0EsncL7GHHaAZOP5YdWnEVLFvMuBGjAQJSLo0/gviz/tq?tqx=out:json&gid=';

export const DEFAULT_SHEET_CODES = [
  { name: 'MONDAY', gid: '1117233428' },
  { name: 'TUESDAY', gid: '1448810901' },
  { name: 'WEDNESDAY', gid: '438851112' },
  { name: 'THURSDAY', gid: '346577696' },
  { name: 'FRIDAY', gid: '2082830326' },
  { name: 'SATURDAY', gid: '985885879' },
];

/**
 * The config JSON can carry codes as an array of {name, gid} or an object
 * mapping {MONDAY: gid}. Normalize both to the array shape used downstream.
 */
export const normalizeSheetCodes = (codes) => {
  if (Array.isArray(codes)) return codes;
  if (codes && typeof codes === 'object') {
    return Object.entries(codes).map(([name, gid]) => ({ name, gid: String(gid) }));
  }
  return [];
};

// Google Sheets JSONP responses are wrapped as `/*O_o*/\n<json>*/`.
// Strip the prefix/suffix instead of relying on magic offsets.
const stripJsonpWrapper = (text) => {
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) {
    throw new Error('Invalid sheet response: no JSON object found');
  }
  return text.slice(start, end + 1);
};

export const parseSheetJson = (text) => {
  const json = JSON.parse(stripJsonpWrapper(text));
  if (!json.table || !Array.isArray(json.table.rows)) {
    throw new Error('Invalid sheet response: missing table.rows');
  }
  return json.table.rows;
};

export const safeParse = (value, fallback) => {
  if (value == null) return fallback;
  try {
    return JSON.parse(value) ?? fallback;
  } catch {
    return fallback;
  }
};

export const GID_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1A1p89c0EsncL7GHHaAZOP5YdWnEVLFvMuBGjAQJSLo0/gviz/tq?tqx=out:json&gid=65012983';

export const fetchSheetConfig = async () => {
  try {
    const res = await fetch(GID_SHEET_URL);
    const text = await res.text();
    const rows = parseSheetJson(text);
    
    const validDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    const codes = [];
    
    rows.forEach((row) => {
      if (!row || !Array.isArray(row.c) || row.c.length < 2) return;
      const name = row.c[0]?.v ? String(row.c[0].v).trim().toUpperCase() : '';
      const gidVal = row.c[1]?.f || (row.c[1]?.v != null ? String(row.c[1].v) : null);
      
      if (validDays.includes(name) && gidVal) {
        codes.push({ name, gid: gidVal });
      }
    });

    const dayOrder = { 'MONDAY': 1, 'TUESDAY': 2, 'WEDNESDAY': 3, 'THURSDAY': 4, 'FRIDAY': 5, 'SATURDAY': 6 };
    codes.sort((a, b) => (dayOrder[a.name] || 99) - (dayOrder[b.name] || 99));

    return {
      url: DEFAULT_SHEET_URL,
      codes: codes.length > 0 ? codes : DEFAULT_SHEET_CODES,
    };
  } catch (error) {
    console.error('Failed to fetch GIDs from sheet, using fallback:', error);
    return {
      url: DEFAULT_SHEET_URL,
      codes: DEFAULT_SHEET_CODES,
    };
  }
};

/**
 * Fetch and parse every day sheet in parallel.
 * Returns an array of `{ sheet, classes }` where each class is
 * `{ val, location, slot, time }` with `time` normalized to a sortable "HH:MM".
 */
export const fetchAllDaySheets = async (sheetUrl, sheetsPageCodes) => {
  const daySheets = await Promise.all(
    sheetsPageCodes.map(async (code) => {
      const res = await fetch(sheetUrl + code.gid);
      const text = await res.text();
      const rows = parseSheetJson(text);

      const classes = [];
      rows.forEach((row, rowIndex) => {
        if (rowIndex < 2 || !Array.isArray(row.c)) return;
        const location = row.c[0]?.v || '';
        row.c.forEach((cell, colIndex) => {
          if (colIndex === 0 || !cell || !cell.v) return;
          const val = String(cell.v);
          let slot = rows[1]?.c?.[colIndex]?.v || '';
          if (val.toLowerCase().includes('lab')) {
            const endTime = rows[1]?.c?.[colIndex + 2]?.v?.split('-')[1];
            if (endTime) slot = `${slot.split('-')[0]}-${endTime}`;
          }
          classes.push({ val, location, slot, time: normalizeTime(slot) });
        });
      });

      return { sheet: code.name, classes };
    })
  );
  return daySheets;
};

/**
 * "9:00" or "09:00" -> "09:00". Slots before 7am are assumed to be PM
 * (FAST runs labs in the evening), so 5:00 -> 17:00.
 */
export const normalizeTime = (slot) => {
  if (!slot) return '';
  const time = slot.split('-')[0];
  const [hourStr, minuteStr] = time.split(':');
  let hour = parseInt(hourStr, 10);
  if (Number.isNaN(hour)) return '';
  if (hour < 7) hour += 12;
  return `${String(hour).padStart(2, '0')}:${(minuteStr || '00').padStart(2, '0')}`;
};

export const sortByTime = (classes) =>
  [...classes].sort((a, b) => {
    if (a.time === b.time) return 0;
    return a.time < b.time ? -1 : 1;
  });

const CACHE_KEY = 'allClasses';
const SAVED_CLASSES_KEY = 'savedClasses';

export const loadCachedClasses = () => safeParse(localStorage.getItem(CACHE_KEY), []);

export const saveCachedClasses = (daySheets) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(daySheets));
  } catch {
    // Storage full or unavailable - non-fatal
  }
};

export const loadSavedClasses = () => safeParse(localStorage.getItem(SAVED_CLASSES_KEY), []);
